import { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, gridClasses, type GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import type { Chat } from '@trinity/chat';
import { TableCell } from './Table.Cell';

interface ExtendedHTMLDivElement extends HTMLDivElement {
  _resizeObserver?: ResizeObserver;
}

type Props = {
  item: Chat.Attachment.Table;
};

type CustomToolbarProps = {
  rows: Array<Record<string, unknown>>;
  columns: GridColDef[];
  headers: string[];
  fileName?: string;
};

const CustomToolbar = ({ rows, columns, headers, fileName = 'table-data' }: CustomToolbarProps) => {
  const handleExport = () => {
    // Convert rows to CSV format
    const csvHeaders = headers.join(',');
    const csvRows = rows.map(row => {
      return headers.map(header => {
        const cellData = row[header];
        const value = typeof cellData === 'string' ? cellData : (cellData as { value?: string })?.value ?? '';
        // Escape quotes and wrap in quotes if contains comma or newline
        const escaped = value.replace(/"/g, '""');
        return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
      }).join(',');
    }).join('\n');

    const csv = `${csvHeaders}\n${csvRows}`;

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <GridToolbarContainer>
      <Button
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleExport}
      >
        Export CSV
      </Button>
    </GridToolbarContainer>
  );
};

export const Table = (props: Props) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const gridRef = useRef<ExtendedHTMLDivElement>(null);

  const attachment = props.item;
  const headers = useMemo(() => attachment?.data.headers || [], [attachment?.data.headers]);
  const pinnedLeft = attachment?.data.pinned?.left ?? [];

  const calculateColumnWidths = useCallback(() => {
    if (gridRef.current) {
      const newWidths: Record<string, number> = {};

      headers.forEach(header => {
        const headerElement = gridRef.current?.querySelector(`[data-field="${header}"]`);
        if (headerElement) {
          newWidths[header] = headerElement.getBoundingClientRect().width;
        }
      });

      setColumnWidths(newWidths);
    }
  }, [headers]);

  const columns: GridColDef[] = useMemo(() => {
    const vals: GridColDef[] = headers.map((x, index) => ({
      field: x,
      type: 'string',
      minWidth: 100,
      maxWidth: 400,
      headerName: x,
      editable: false,
      hideable: true,
      valueGetter: (_value, row: Record<string, Chat.AttachmentTableRow>) => {
        const cellData = row[x];
        return typeof cellData === 'string' ? cellData : (cellData?.value ?? '');
      },
      renderCell: TableCell,
    }));
    return vals;
  }, [headers]);

  const rows = useMemo(() => {
    if (!attachment?.data.rows) {
      return [];
    }
    const vals = attachment?.data.rows.map((x, i) => ({
      ...x,
      _gridId: `${props.item.id}-${i}-${i}`,
    }));
    return vals;
  }, [attachment?.data.rows, props.item.id]);

  useLayoutEffect(() => {
    calculateColumnWidths();
  }, [headers, rows, calculateColumnWidths]);

  useEffect(() => {
    return () => {
      if (gridRef.current) {
        const observer = gridRef.current._resizeObserver;

        if (observer) {
          observer.disconnect();
        }
      }
    };
  }, []);

  const gridCallbackRef = useCallback((node: ExtendedHTMLDivElement | null) => {
    if (gridRef.current && gridRef.current !== node) {
      const observer = gridRef.current._resizeObserver;

      if (observer) {
        observer.disconnect();
        delete gridRef.current._resizeObserver;
      }
    }

    gridRef.current = node;

    if (node) {
      const resizeObserver = new ResizeObserver(() => {
        calculateColumnWidths();
      });

      resizeObserver.observe(node);
      node._resizeObserver = resizeObserver;

      calculateColumnWidths();
    }
  }, [calculateColumnWidths]);

  return (
    <div ref={gridCallbackRef} style={{ width: '100%', overflowX: 'auto', marginTop: '16px' }}>
      <DataGridStriped
        pinnedColumns={pinnedLeft}
        columnWidths={columnWidths}
        disableRowSelectionOnClick={true}
        rowSelection={false}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._gridId}
        getRowClassName={params => params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'}
        autosizeOptions={{
          includeHeaders: true,
          includeOutliers: true,
        }}
        autosizeOnMount={true}
        getRowHeight={() => 'auto'}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            rows,
            columns,
            headers,
            fileName: attachment?.data.title || 'table-data',
          } as CustomToolbarProps,
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 'bold !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold !important',
          },
        }} />
    </div>
  );
};

Table.displayName = 'Chat.Attachments.Table';

const DataGridStriped = styled(DataGrid)<{ pinnedColumns: string[]; columnWidths: Record<string, number> }>(({ theme: _, pinnedColumns, columnWidths }) => {
  const pinnedStyles: Record<string, unknown> = {};

  let cumulativeLeft = 0;
  pinnedColumns.forEach((field, index) => {
    pinnedStyles[`& .MuiDataGrid-cell[data-field="${field}"], & .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
      position: 'sticky',
      left: cumulativeLeft,
      backgroundColor: 'inherit',
      zIndex: pinnedColumns.length - index + 1,
      borderRight: index === pinnedColumns.length - 1 ? '2px solid #e0e0e0' : '1px solid #e0e0e0',
    };

    pinnedStyles[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
      position: 'sticky',
      left: cumulativeLeft,
      backgroundColor: '#ffffff !important',
      zIndex: pinnedColumns.length - index + 2,
      borderRight: index === pinnedColumns.length - 1 ? '2px solid #e0e0e0' : '1px solid #e0e0e0',
      borderLeft: 'none !important',
      boxShadow: 'none !important',
      '&::before': {
        display: 'none !important',
      },
      '&::after': {
        display: 'none !important',
      },
      '& .MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
      '& .MuiDataGrid-iconSeparator': {
        display: 'none !important',
      },
    };

    pinnedStyles[`& .MuiDataGrid-columnHeader[data-field="${field}"] .MuiDataGrid-columnSeparator`] = {
      display: 'none !important',
    };

    cumulativeLeft += columnWidths[field] || 100;
  });

  return {
    width: '100%',
    minHeight: 400,
    '& .MuiDataGrid-root': {
      overflow: 'auto',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      display: 'none !important',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderRight: 'none !important',
    },
    ...pinnedStyles,
    [`& .${gridClasses.row}.odd`]: {
      backgroundColor: '#f6f8fa',
      '&:hover': {
        backgroundColor: '#f6f8fa',
      },
      '&.Mui-selected': {
        backgroundColor: '#f6f8fa',
        '&:hover': {
          backgroundColor: '#f6f8fa',
        },
      },
    },
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
      '&.Mui-selected': {
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#ffffff',
        },
      },
    },
    [`& .${gridClasses.cell}`]: {
      outline: 'transparent',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.4',
      padding: '8px',
    },
    [`& .${gridClasses.cell}:focus-within`]: {
      outline: 'none',
    },
    [`& .${gridClasses.columnHeader}`]: {
      fontWeight: 'bold',
    },
  };
});
